/*
 * PhoneNumberInput
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp, switchProp } from 'styled-tools';
import { cond, propEq, T } from 'ramda';

import PhoneInput from 'react-phone-number-input';

import 'react-phone-number-input/style.css';

export type PhoneNumberInputProps = {
  value?: string,
  placeholder?: string,
  onChange?: () => void,
  onFocus?: () => void,
  onBlur?: () => void,
  country?: string,
  size?: string,
  disabled?: boolean,
  focused?: boolean,
};

const border = cond([
  [propEq('disabled', true), palette('grayscale', 4)],
  [propEq('focused', true), palette('primary', 1)],
  [T, palette('grayscale', 2)],
]);

const Wrapper = styled.div`
  .react-phone-number-input {
    font-size: ${ifProp('size', '15px', '17px')};
    display: inline-flex;
    position: relative;
    padding: 10px 0 20px;
    align-items: center;
    cursor: ${ifProp('disabled', 'default', 'pointer')};
    ${switchProp('size', {
      small: css`
        height: 32px;
        font-size: 15px;
      `,
      medium: css`
        height: 40px;
        font-size: 16px;
      `,
      large: css`
        height: 50px;
        font-size: 16px;
      `,
    })};
    min-width: 180px;
    border-radius: 25px;

    &__icon {
      width: 25px;
      height: 20px;

      img,
      svg {
        height: 100%;
        width: 100%;
      }
    }

    &__row {
      position: relative;
      height: 100%;
      border: 1px solid ${border};
      overflow: hidden;
      border-radius: 25px;
    }

    &__country {
      position: absolute;
      height: 100%;
      left: 15px;
    }

    &__input {
      border: none;
      height: 100%;
      padding-left: 60px;
      padding-right: 15px;
      color: #6a6b71;
    }
  }
`;

/**
 * PhoneNumberInput React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {string} [props.value] - input value
 * @param {string} [props.country] - preselected country
 * @param {string} [props.placeholder] - input placeholder
 * @param {Function} [props.onChange = () => {}] - on change value callback
 * @param {Function} [props.onFocus = () => {}] - on focus change callback
 * @param {Function} [props.onBlur = () => {}] - on blur change callback
 * @param {('medium'|'small'|'large')} [size = 'medium'] - set input size
 * @param {boolean} [disabled] - disabled state
 * @param {boolean} [focused] - focused state
 * @example <caption>Simple usage</caption>
 * <PhoneNumberInput />
 * @returns {React.Node<*>} React-component
 */
export const PhoneNumberInput = ({
  size,
  focused,
  ...rest
}: PhoneNumberInputProps) => (
  <Wrapper
    size={size}
    disabled={rest.disabled}
    focused={focused}
    testid="phone-number-input"
  >
    <PhoneInput {...rest} />
  </Wrapper>
);

PhoneNumberInput.defaultProps = {
  size: 'medium',
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {},
};
